import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { statsApi } from '../api/endpoints';
import styles from './Dashboard.module.css';

/**
 * Главная страница админки.
 *
 * Раньше здесь были три кнопки и больше ничего: ни объёма фонда, ни того,
 * что добавлялось последним, ни признаков незаполненных записей.
 */
const Dashboard = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        statsApi
            .get()
            .then((d) => !cancelled && setData(d))
            .catch((e) => !cancelled && setError(e));

        return () => {
            cancelled = true;
        };
    }, []);

    if (error) {
        return (
            <div className="adm-card adm-card-body">
                <div className={styles.errorTitle}>Не удалось загрузить сводку</div>
                <p className="adm-muted">{error.message}</p>
            </div>
        );
    }

    const loading = !data;

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">Обзор</h1>
                    <p className="adm-page-subtitle">Состояние библиотеки и последние добавления</p>
                </div>
            </div>

            <div className={styles.cards}>
                {(loading ? PLACEHOLDER_SECTIONS : data.sections).map((s, i) => (
                    <NavLink key={s.key || i} to={loading ? '/admin' : s.link} className={`adm-card ${styles.card}`}>
                        <div className={styles.cardTitle}>{s.title}</div>
                        <div className={styles.cardValue}>
                            {loading ? <span className={styles.skeletonValue} /> : s.total}
                        </div>
                        {!loading && s.note && <div className={styles.cardNote}>{s.note}</div>}
                    </NavLink>
                ))}
            </div>

            {!loading && data.issues && data.issues.length > 0 && (
                <div className={`adm-card ${styles.issues}`}>
                    <div className="adm-card-header">
                        <h2 className="adm-card-title">Требует внимания</h2>
                    </div>
                    <ul className={styles.issueList}>
                        {data.issues.map((issue, i) => (
                            <li key={i}>
                                <span className="adm-badge adm-badge-warning">!</span>
                                <NavLink to={issue.link}>{issue.title}</NavLink>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className={styles.columns}>
                <RecentList
                    title="Последние статьи"
                    link="/admin/articles"
                    items={loading ? null : data.recent.articles}
                    renderMeta={(item) =>
                        item.view === 0 ? <span className="adm-badge adm-badge-neutral">скрыта</span> : null
                    }
                />
                <RecentList
                    title="Последние учебные издания"
                    link="/admin/educational"
                    items={loading ? null : data.recent.educational}
                />
                <RecentList
                    title="Последние диссертации"
                    link="/admin/disertations"
                    items={loading ? null : data.recent.disertations}
                />
            </div>

            {!loading && (
                <div className={`adm-card ${styles.dictionaries}`}>
                    <div className="adm-card-header">
                        <h2 className="adm-card-title">Справочники</h2>
                    </div>
                    <div className={styles.dictionaryGrid}>
                        {data.dictionaries.map((d) => (
                            <NavLink key={d.link} to={d.link} className={styles.dictionaryItem}>
                                <span>{d.title}</span>
                                <span className={styles.dictionaryCount}>{d.total}</span>
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

const RecentList = ({ title, link, items, renderMeta }) => (
    <div className={`adm-card ${styles.recent}`}>
        <div className="adm-card-header">
            <h2 className="adm-card-title">{title}</h2>
            <NavLink to={link} className={styles.allLink}>
                Все →
            </NavLink>
        </div>

        {items === null && (
            <div className={styles.recentBody}>
                {[0, 1, 2].map((i) => (
                    <div key={i} className={styles.skeletonRow} />
                ))}
            </div>
        )}

        {items && items.length === 0 && (
            <div className={`${styles.recentBody} adm-muted`}>Пока ничего не добавлено</div>
        )}

        {items && items.length > 0 && (
            <ul className={styles.recentList}>
                {items.map((item) => (
                    <li key={item.id}>
                        <NavLink to={item.link} className={styles.recentName} title={item.name}>
                            {item.name || `#${item.id}`}
                        </NavLink>
                        <span className={styles.recentMeta}>
                            {renderMeta && renderMeta(item)}
                            {item.date && <span className="adm-muted">{formatDate(item.date)}</span>}
                        </span>
                    </li>
                ))}
            </ul>
        )}
    </div>
);

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('ru-RU');
};

const PLACEHOLDER_SECTIONS = [
    { title: 'Статьи' },
    { title: 'Журналы' },
    { title: 'Учебные издания' },
    { title: 'Диссертации' },
];

export default Dashboard;
